<!-- eslint-disable no-empty -->
<template>
  <div
    class="max-w-xl w-full sm:rounded-lg sm:shadow-lg px-6 py-10
    flex flex-col items-center justify-center"
  >
    <Logo class="place-self-center mb-6" />
    <p
      class="sm:text-2xl text-xl mb-4"
      style="color: #000000;"
    >
      {{ signInMessages.title }}
    </p>

    <div class="w-80 flex-col justify-center space-y-4">
      <div class="space-y-1">
        <label
          for="login-email"
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          * {{ $t('message.email_address') }}
        </label>
        <Input
          id="login-email"
          v-model="email"
          type="email"
          autocomplete="email"
          autofocus
          :placeholder="$t('message.email_placeholder')"
          :error="emailFieldError"
          :error-message="emailErrorMessage"
          :valid="emailIsValid"
          :aria-label="$t('message.email_address')"
          aria-required="true"
          @blur="markFieldTouched('email')"
        />
      </div>

      <div class="space-y-1">
        <label
          for="login-password"
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          * {{ $t('message.password') }}
        </label>
        <PasswordInput
          id="login-password"
          v-model="password"
          autocomplete="current-password"
          :placeholder="$t('message.your_password')"
          :error="passwordFieldError"
          :error-message="passwordErrorMessage"
          :aria-label="$t('message.password')"
          aria-required="true"
          @blur="markFieldTouched('password')"
        />
        <div class="flex justify-end">
          <button
            v-if="signInUtils.showForgotPassword"
            type="button"
            class="text-xs cursor-pointer bg-transparent border-0 p-0
              underline hover:opacity-80 rounded font-bold"
            :style="{ color: linkColor }"
            :aria-label="$t('message.accessibility.forgot_password')"
            @click="switchToForgotPassword"
          >
            {{ signInMessages.forgotPassword }}
          </button>
        </div>
        <div
          class="flex pt-2"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          <span
            class="text-xs font-bold"
            v-html="$t('message.sign_in_privacy_terms', {
              color: linkColor, privacyUrl: global.privacyUrl
            })"
          />
          <span
            class="text-xs font-bold"
            v-html="$t('message.sign_in_general_conditions', {
              color: linkColor, generalConditionsUrl: global.generalConditionsUrl
            })"
          />
        </div>
      </div>

      <Button
        class="w-full"
        type="primary"
        @onClick="onSignInPressWithValidation"
      >
        {{ signInMessages.btnText }}
      </Button>
      <Message
        v-if="message && message.type && message.text"
        class="w-full"
        :type="message.type"
        :text="message.type === 'error' ? formatLoginErrorMessage(message.text) : message.text"
      />
      <div
        v-if="signInUtils.showSocialLogin"
        class="flex flex-col space-y-4"
      >
        <p
          class="text-lg"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          {{ signInMessages.labelSocialLogin }}
        </p>
        <div class="flex justify-center align-middle space-x-6">
          <button
            v-if="signInUtils.showFacebookLogin"
            type="button"
            class="cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
            :aria-label="$t('message.accessibility.login_with_facebook')"
            :title="$t('message.accessibility.login_with_facebook')"
            @click="handleFacebookSignIn"
          >
            <FacebookButton class="w-8 h-8" />
          </button>
          <button
            v-if="signInUtils.showGoogleLogin"
            type="button"
            class="cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
            :aria-label="$t('message.accessibility.login_with_google')"
            :title="$t('message.accessibility.login_with_google')"
            @click="handleGoogleSignIn"
          >
            <GoogleButton class="w-8 h-8" />
          </button>
          <button
            v-if="signInUtils.showAppleLogin"
            type="button"
            class="cursor-pointer -mt-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
            :aria-label="$t('message.accessibility.login_with_apple')"
            :title="$t('message.accessibility.login_with_apple')"
            @click="handleAppleSignIn"
          >
            <AppleButton class="w-9 h-9" />
          </button>
        </div>
      </div>
    </div>
  </div>
  <RegistrationRequestPanel v-if="signInUtils.showRegistrationPanel" />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import RegistrationRequestPanel from '@/components/registration/RegistrationRequestPanel.vue';
import Button from '@/components/atoms/Button.vue';
import Input from '@/components/atoms/Input.vue';
import { mapActions, mapGetters } from 'vuex';
import { redirectToCallbackURL, googleSignIn, facebookSignIn, appleSignIn, postMessageToParent, isOtpRequired } from '@/service/cognitoService';
import { InputValidator } from '@/utils';
import Message from '@/components/atoms/Message.vue';
import GoogleButton from '@/components/atoms/GoogleButton.vue';
import FacebookButton from '@/components/atoms/FacebookButton.vue';
import AppleButton from '@/components/atoms/AppleButton.vue';
import Logo from '@/components/atoms/Logo.vue';
import PasswordInput from '@/components/atoms/PasswordInput.vue';
import { CognitoException } from '@/model/cognito';
import { AppStatus } from '@/model/status';
import { MessageType, AlertMessage } from '@/model/message';
import { ROUTES_NAMES, SOCIAL_PROVIDERS } from '@/constants';

export default defineComponent({
  name: 'LoginPanel',
  components: {
    RegistrationRequestPanel,
    Button,
    Input,
    Message,
    GoogleButton,
    FacebookButton,
    AppleButton,
    Logo,
    PasswordInput
  },
  data () {
    return {
      event: {
        socialLogin: 'social_login'
      },
      password: '',
      email: '',
      loginTryCount: parseInt(sessionStorage.getItem('loginTryCount') ?? '0'),
      message: {} as AlertMessage | undefined,
      appStatusToMessageMap:
        this.initAppStatusToMessageMap() as Map<AppStatus, AlertMessage>,
      cognitoErrorsToMessageMap:
        this.initCognitoErrorsToMessageMap() as Map<CognitoException, AlertMessage>,
      showValidationErrors: false,
      touchedFields: {
        email: false,
        password: false
      }
    };
  },
  computed: {
    ...mapGetters([
      'global',
      'signInMessages',
      'signInUtils',
      'myEmail',
      'appStatus',
      'isIframe',
      'idToken',
      'accessToken',
      'refreshToken'
    ]),
    linkColor (): string {
      return this.global.linkColor || this.global.primaryColor;
    },
    enableSignInButton (): boolean {
      return InputValidator.validateEmail(this.email) && !!this.password;
    },
    checkEmailError (): boolean {
      return this.email ? !InputValidator.validateEmail(this.email) : false;
    },
    emailIsValid (): boolean {
      return this.email ? InputValidator.validateEmail(this.email) : false;
    },
    emailFieldError (): boolean {
      return this.shouldShowFieldError('email') && (!this.email || this.checkEmailError);
    },
    emailErrorMessage (): string {
      if (!this.emailFieldError) {
        return '';
      }

      return this.email
        ? this.formatLoginErrorMessage(this.$t('message.accessibility.email_invalid') as string)
        : this.formatLoginErrorMessage(this.$t('message.accessibility.email_required') as string);
    },
    passwordFieldError (): boolean {
      return this.shouldShowFieldError('password') && !this.password;
    },
    passwordErrorMessage (): string {
      if (!this.passwordFieldError) {
        return '';
      }

      return this.formatLoginErrorMessage(this.$t('message.accessibility.password_required') as string);
    }
  },
  mounted () {
    if (this.myEmail) {
      this.email = this.myEmail;
      this.setMyEmail('');
    }
    if (this.appStatus) {
      this.message = this.appStatusToMessageMap.get(this.appStatus) as AlertMessage;
      this.setAppStatus('');
    }
  },
  methods: {
    ...mapActions(['signIn', 'setMyEmail', 'setAppStatus', 'startLoading', 'endLoading']),

    formatLoginErrorMessage (text: string): string {
      return text.startsWith('* ') ? text : `* ${text}`;
    },

    markFieldTouched (field: string): void {
      (this.touchedFields as Record<string, boolean>)[field] = true;
    },

    shouldShowFieldError (field: string): boolean {
      return this.showValidationErrors || Boolean((this.touchedFields as Record<string, boolean>)[field]);
    },

    onSignInPressWithValidation (): void {
      this.showValidationErrors = true;

      if (!this.email) {
        return;
      }

      if (this.checkEmailError || !this.password) {
        return;
      }

      this.showValidationErrors = false;
      this.onSignInPress();
    },

    initAppStatusToMessageMap (): Map<AppStatus, AlertMessage> {
      return new Map([
        [AppStatus.UserAlreadyExists, {
          type: MessageType.Error,
          text: this.$t('message.user_already_exists_error')
        }],
        [AppStatus.ConfirmSignUpSuccess, {
          type: MessageType.Success,
          text: this.$t('message.confirmation_sign_up_success_message')
        }],
        [AppStatus.ConfirmSignUpError, {
          type: MessageType.Error,
          text: this.$t('message.confirmation_sign_up_error_message')
        }],
        [AppStatus.ResetPasswordSuccess, {
          type: MessageType.Success,
          text: this.$t('message.reset_password_success')
        }],
        [AppStatus.ResetPasswordError, {
          type: MessageType.Error,
          text: this.$t('message.reset_password_error')
        }]
      ]);
    },

    initCognitoErrorsToMessageMap (): Map<CognitoException, AlertMessage> {
      return new Map([
        [CognitoException.UsernameExistsException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_user_not_confirmed_error')
        }],
        [CognitoException.UserLambdaValidationException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_try_later_error')
        }],
        [CognitoException.NotAuthorizedException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_not_authorized_error')
        }],
        [CognitoException.UserNotConfirmedException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_user_not_confirmed_error')
        }],
        [CognitoException.UserNotFoundException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_user_not_found_error')
        }]
      ]);
    },

    async onSignInPress (): Promise<void> {
      try {
        this.message = undefined;
        this.startLoading();
        await this.signIn({
          email: this.email.toLowerCase(),
          password: this.password
        });
        sessionStorage.removeItem('loginTryCount');

        const otpStepRequired = await isOtpRequired();
        if(otpStepRequired) {
          this.switchToOTP();
          return;
        }

        this.isIframe
          ? postMessageToParent({
            idToken: this.idToken,
            accessToken: this.accessToken,
            refreshToken: this.refreshToken
          })
          : redirectToCallbackURL();
      } catch (error: any) {
        this.endLoading();
        const errorCode = error.code || error?.response?.data?.__type;

        if (errorCode === CognitoException.PasswordResetRequiredException) {
          this.setAppStatus(AppStatus.ResetPasswordRequired);
          this.switchToForgotPassword();
          return;
        }
        this.loginTryCount++;
        sessionStorage.setItem('loginTryCount', this.loginTryCount.toString());
        if(this.loginTryCount > 3) {
          this.message = {
            type: MessageType.Error,
            text: this.$t('message.sign_in_3_time_limit')
          };
          return;
        }
        this.message = this.cognitoErrorsToMessageMap.get(errorCode) as AlertMessage;
      }
    },
    async handleGoogleSignIn (): Promise<void> {
      try {
        this.startLoading();
        if (this.isIframe) {
          return postMessageToParent({
            event: this.event.socialLogin,
            provider: SOCIAL_PROVIDERS.GOOGLE
          });
        }
        await googleSignIn();
      } catch (error) {
      } finally {
        this.endLoading();
      }
    },
    async handleFacebookSignIn (): Promise<void> {
      try {
        this.startLoading();
        if (this.isIframe) {
          return postMessageToParent({
            event: this.event.socialLogin,
            provider: SOCIAL_PROVIDERS.FACEBOOK
          });
        }
        await facebookSignIn();
      } catch (error) {
      } finally {
        this.endLoading();
      }
    },
    async handleAppleSignIn (): Promise<void> {
      try {
        this.startLoading();
        if (this.isIframe) {
          return postMessageToParent({
            event: this.event.socialLogin,
            provider: SOCIAL_PROVIDERS.APPLE
          });
        }
        await appleSignIn();
      } catch (error) {
      } finally {
        this.endLoading();
      }
    },
    switchToForgotPassword (): void {
      if (this.email) {
        this.setMyEmail(this.email);
      }
      this.$router.push({
        path: 'forgotpassword',
        query: {
          ...this.$route.query
        }
      });
    },
    switchToOTP (): void {
      this.$router.push({
        path: ROUTES_NAMES.otp,
        query: {
          ...this.$route.query
        }
      });
    }
  }
});
</script>
